import { useEffect, useMemo, useState } from "react";
import { venueName } from "../venues";
import { assetDp, Banner, Card, CardHead, Empty, Freshness, Num, Pill, Skeleton, type Tone } from "../components/ui";
import { IconOrders } from "../components/icons";

interface OrderRow {
  id: string;
  external_id: string;
  order_type: string;
  from_asset: string;
  to_asset: string;
  from_amount: string | null;
  to_amount: string | null;
  status: string;
  client_reference: string | null;
  submitted_at: string;
  client_name: string | null;
  exec_venue: string | null;
  exec_status: string | null;
  executed_price: string | null;
  markup_revenue: string | null;
  exec_detail: string | null;
}

const auth = () => ({ authorization: `Bearer ${sessionStorage.getItem("otc.session") ?? ""}` });
const POLL_MS = 30_000;

// Status colour is semantic, not decorative: anything terminal-and-wrong is
// red, anything needing a person is amber, anything in flight is neutral so
// it does not compete with the two that matter.
const STATUS_TONE: Record<string, Tone> = {
  received: "neutral",
  validating: "neutral",
  executing: "info",
  awaiting_approval: "warn",
  halted: "warn",
  completed: "good",
  rejected: "bad",
  failed: "bad",
};

const FILTERS = ["all", "in flight", "completed", "attention"] as const;
type Filter = (typeof FILTERS)[number];

function inFilter(o: OrderRow, f: Filter): boolean {
  if (f === "all") return true;
  if (f === "completed") return o.status === "completed";
  if (f === "attention") return ["awaiting_approval", "halted", "failed", "rejected"].includes(o.status);
  return ["received", "validating", "executing"].includes(o.status);
}

export function Orders() {
  const [rows, setRows] = useState<OrderRow[] | null>(null);
  const [error, setError] = useState("");
  const [at, setAt] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch("/api/orders", { headers: auth() })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((d) => {
          if (!alive) return;
          setRows(d.orders);
          setAt(Date.now());
          setError("");
        })
        .catch(() => alive && setError("Could not load orders."));
    void load();
    const t = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const counts = useMemo(() => {
    const all = rows ?? [];
    return Object.fromEntries(FILTERS.map((f) => [f, all.filter((o) => inFilter(o, f)).length])) as Record<Filter, number>;
  }, [rows]);

  const shown = (rows ?? []).filter((o) => inFilter(o, filter));

  if (!rows && !error) return <Skeleton rows={7} />;

  return (
    <>
      <p className="page-intro">
        Orders received from Xchange360 over the signed order API, with the execution the desk achieved against each.
      </p>

      {error && <Banner tone="error">{error}</Banner>}

      <Card pad={false}>
        <CardHead
          title="Order book"
          right={
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="seg">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`btn btn-sm ${filter === f ? "btn-secondary" : "btn-ghost"}`}
                    onClick={() => setFilter(f)}
                    aria-pressed={filter === f}
                    style={{ textTransform: "capitalize" }}
                  >
                    {f}
                    {counts[f] > 0 && <span className="dim num" style={{ marginLeft: 5 }}>{counts[f]}</span>}
                  </button>
                ))}
              </div>
              <Freshness at={at} intervalMs={POLL_MS} />
            </div>
          }
        />

        {shown.length === 0 ? (
          <Empty
            icon={<IconOrders size={20} />}
            title={rows?.length === 0 ? "No orders yet" : `Nothing ${filter}`}
            body={
              rows?.length === 0
                ? "Orders appear the moment Xchange360 submits its first signed order."
                : "Try another filter. The desk has orders in other states."
            }
          />
        ) : (
          <div className="table-wrap flush">
            <table>
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Client</th>
                  <th>Pair</th>
                  <th className="right">Amount</th>
                  <th>Status</th>
                  <th>Execution</th>
                  <th className="right">Markup</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((o) => (
                  <tr key={o.id}>
                    <td className="dim num" style={{ whiteSpace: "nowrap" }}>
                      {new Date(o.submitted_at).toLocaleString([], {
                        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.client_name ?? <span className="dimmer">unknown</span>}
                    </td>
                    <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                      {o.from_asset} <span className="dimmer">→</span> {o.to_asset}
                    </td>
                    <td className="right num" style={{ whiteSpace: "nowrap" }}>
                      {o.from_amount
                        ? <><Num value={o.from_amount} dp={assetDp(o.from_asset)} /> <span className="dim">{o.from_asset}</span></>
                        : <><Num value={o.to_amount} dp={assetDp(o.to_asset)} /> <span className="dim">{o.to_asset}</span></>}
                    </td>
                    <td>
                      <Pill tone={STATUS_TONE[o.status] ?? "neutral"} dot>
                        {o.status.replaceAll("_", " ")}
                      </Pill>
                    </td>
                    <td className="t-sm">
                      {o.exec_status === "filled" ? (
                        <span style={{ whiteSpace: "nowrap" }}>
                          <strong style={{ fontWeight: 500 }}>{venueName(o.exec_venue)}</strong>
                          <span className="dim num"> @ <Num value={o.executed_price} dp={2} /></span>
                        </span>
                      ) : o.exec_status ? (
                        <span className="dim" title={o.exec_detail ?? ""}>{o.exec_status}</span>
                      ) : (
                        <span className="dimmer">—</span>
                      )}
                    </td>
                    <td className="right num">
                      {o.markup_revenue
                        ? <span className="gain">+<Num value={o.markup_revenue} dp={2} /></span>
                        : <span className="dimmer">—</span>}
                    </td>
                    <td className="dim t-sm" style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.client_reference ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
